import React, { useState, useEffect } from 'react';
import { Heart, Menu, X, User, LogOut } from 'lucide-react';
import { Drawer, IconButton, Tooltip } from '@mui/material';
import { FaInstagram, FaFacebook, FaYoutube, FaTwitter, FaPinterest, FaLinkedin, FaWhatsapp, FaGem } from "react-icons/fa";
import { Link } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { useUserAuth } from '../context/UserAuthContext';

import './Navbar.css';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [logoSrc, setLogoSrc] = useState('/media/logo.png');
  const { wishlist } = useWishlist();
  const { currentUser, isAuthenticated, logout } = useUserAuth();
  const [showTooltip, setShowTooltip] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hide tooltip after 10 seconds
  useEffect(() => {
    const tooltipTimer = setTimeout(() => {
      setShowTooltip(false);
    }, 10000);
    
    return () => clearTimeout(tooltipTimer);
  }, []);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  const getUserDisplayName = () => {
    if (!currentUser) return '';
    return currentUser.firstName || currentUser.email.split('@')[0];
  };

  const tryAlternateLogoPath = (currentSrc) => {
    if (currentSrc === '/media/logo.png') {
      return 'https://nmdiamond.in/media/logo.png';
    } else if (currentSrc.includes('nmdiamond.in')) {
      return '/logo.png';
    }
    return null;
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        {/* Logo */}
        <Link to="/" className="navbar-logo">
          {!logoError ? (
            <img 
              src={logoSrc}
              alt="NM Diamond Logo" 
              className="logo-image" 
              onError={(e) => {
                const nextPath = tryAlternateLogoPath(e.target.src);
                if (nextPath) {
                  console.log('Trying alternate logo path:', nextPath);
                  setLogoSrc(nextPath);
                } else {
                  console.error('Logo failed to load after all attempts');
                  setLogoError(true);
                }
              }}
            />
          ) : (
            <div className="logo-fallback">NM</div>
          )}
          <div className="logo-text">
            <h1 className="logo-title">NM Diamond</h1>
            <p className="logo-subtitle">Diamonds & Jewelry</p>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="nav-links">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/collections" className="nav-link">Collections</Link>
          <Link to="/new-arrivals" className="nav-link">New Arrivals</Link>
          <Link to="/diamonds" className="nav-link">Diamonds</Link>
          <Link to="/diamond-matchmaker" className="nav-link ai-matchmaker-link">AI Matchmaker</Link>
          <Link to="/offers" className="nav-link special-offers-link" data-text="Special Offers">Special Offers</Link>
        </div>

        {/* Icons */}
        <div className="nav-icons">
          <Tooltip 
            title="Evaluate Diamond Prices" 
            arrow 
            placement="bottom" 
            open={showTooltip}
            disableFocusListener={!showTooltip}
            disableHoverListener={!showTooltip}
            disableTouchListener={!showTooltip}
          >
            <Link to="/diamonds" className="diamond-evaluator-btn" aria-label="Diamond Price Evaluator">
              <FaGem size={22} className="diamond-icon" />
            </Link>
          </Tooltip>
          <Link to="/wishlist" className="wishlist-icon-container">
            <Heart size={24} className="nav-icon" />
            {wishlist.length > 0 && (
              <span className="wishlist-count">{wishlist.length}</span>
            )}
          </Link>
          
          {isAuthenticated ? (
            <div className="user-profile">
              <span className="user-greeting">Hello, {getUserDisplayName()}</span>
              <button onClick={handleLogout} className="logout-button" aria-label="Logout">
                <LogOut size={20} />
              </button>
            </div>
          ) : (
            <Link to="/auth" className="auth-link">
              <User size={24} className="nav-icon" />
            </Link>
          )}
          
          <Menu
            size={24}
            className="mobile-menu-icon"
            onClick={() => setIsMobileMenuOpen(true)}
          />
        </div>
      </div>

      
      <Drawer anchor="left" open={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)}>
        <div className="mobile-menu">
          <IconButton onClick={() => setIsMobileMenuOpen(false)} className="close-button">
            <X size={24} />
          </IconButton>
          
          {isAuthenticated && (
            <div className="mobile-user-profile">
              <User size={24} />
              <span>Hello, {getUserDisplayName()}</span>
            </div>
          )}
          
          <div className="mobile-links">
            <Link to="/" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
            <Link to="/collections" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>Collections</Link>
            <Link to="/new-arrivals" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>New Arrivals</Link>
            <Link to="/diamonds" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>Diamonds</Link>
            <Link to="/offers" className="mobile-link special-offers-link" data-text="Special Offers" onClick={() => setIsMobileMenuOpen(false)}>Special Offers</Link>
            
            <Link to="/diamond-matchmaker" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>
              AI Diamond Matchmaker
            </Link>
            <Link to="/wishlist" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>
              Wishlist {wishlist.length > 0 && `(${wishlist.length})`}
            </Link>
            {isAuthenticated ? (
              <button onClick={handleLogout} className="mobile-link logout-mobile-link">
                Logout
              </button>
            ) : (
              <Link to="/auth" className="mobile-link" onClick={() => setIsMobileMenuOpen(false)}>
                Login / Sign Up
              </Link>
            )}
          </div>
          
          <button className="appointment-button" onClick={() => window.open("https://wa.me/919205894678", "_blank")}>Book Appointment</button>
          <div className="mobile-social-icons">
            <a href="https://www.facebook.com/share/18q7zwNDVB/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">
              <FaFacebook className="social-icon" />
            </a>
            <a href="https://www.instagram.com/nmdiamondofficial" target="_blank" rel="noopener noreferrer">
              <FaInstagram className="social-icon" />
            </a>
            <a href="https://youtube.com/@nmdiamond-z9u?feature=shared" target="_blank" rel="noopener noreferrer">
              <FaYoutube className="social-icon" />
            </a>
            <a href="https://wa.me/919205894678" target="_blank" rel="noopener noreferrer">
              <FaWhatsapp className="social-icon" />
            </a>
          </div>
        </div>
      </Drawer>
    </nav>
  );
};

export default Navbar;
